/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {DOCUMENT, Inject, Injectable} from '@angular/core';
import * as _ from 'lodash';

import {AUTH_EVENT} from '../auth/auth-constants';
import {getRegisteredUrl} from '../../app/config/registered-urls';
import {ElectionApiService} from '../../app/common/controller/election-api.service';
import {NetworkInfoApiService} from '../../app/base/simple/network-info-api.service';
import {LocalNetworkInfo} from '../session/local-network-info';
import {LocalNetworkProfile} from '../session/local-network-profile';
import {LocalInactiveTargets} from '../session/local-inactive-targets';
import {LocalUserProfile} from '../session/local-user-profile';
import {AdnFormStateService} from './adn-form-state.service';
import {WorkspaceRegistryService} from '../workspace-registry.service';
import {WINDOW} from '../../app/angular-platform-providers';
import {AuthHttpBufferService} from '../auth/auth-http-buffer.service';
import {ApiConfig} from '../../app/config/api-config';

@Injectable({
  providedIn: 'root'
})
export class AdnNetworkHelperService {
  constructor(
    @Inject(WINDOW) private readonly window: any,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly WorkspaceRegistry: WorkspaceRegistryService,
    private readonly LocalNetworkProfile: LocalNetworkProfile,
    @Inject(NetworkInfoApiService) private readonly NetworkInfo: NetworkInfoApiService,
    private readonly localUserProfile: LocalUserProfile,
    private readonly adnFormState: AdnFormStateService,
    private readonly electionApi: ElectionApiService,
    private readonly authHttpBuffer: AuthHttpBufferService,
    private readonly apiConfig: ApiConfig,
    private readonly localInactiveTargets: LocalInactiveTargets,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
  }

  switchNetwork(baseCtrl: any, eventBus: any, network: any): any {
    const previousNetwork = {
      networkId: this.LocalNetworkProfile.get('networkId'),
      tagId: this.LocalNetworkProfile.get('tagId'),
      defaultNetworkTagId: this.localUserProfile.get('defaultNetworkTagId')
    };

    this.setNetworkSwitchInProgress(true);

    this.localUserProfile.set('defaultNetworkTagId', network.tagId);
    this.LocalNetworkProfile.beginNetworkSwitch(network.tagId, network.id);
    this.localNetworkInfo.clearNetworkCache();
    this.localInactiveTargets.clear();

    const regUrl = getRegisteredUrl(this.window, this.apiConfig, this.localNetworkInfo, network.id);
    this.localNetworkInfo.setSuppInfo('themeId', _.get(regUrl, ['id'], ''));

    const nInfoProm = firstValueFrom(this.NetworkInfo.get()).then((data: any) => {
      this.localNetworkInfo.setInfo(data);
    });

    const electionProm = firstValueFrom(this.electionApi.list({includeTests: true})).then((data: any) => {
      this.localNetworkInfo.setSuppInfo('elections', data.results);
    });

    return Promise.all([
      this.LocalNetworkProfile.refresh(),
      this.WorkspaceRegistry.sync(),
      nInfoProm,
      electionProm
    ]).then(() => {
      if (baseCtrl) {
        baseCtrl.currentNetwork = network;
      }
      eventBus.emit(AUTH_EVENT.networkChange, network);
      return new Promise<void>(resolve => {
        setTimeout(() => {
          this.reloadApp();
          resolve();
        }, 300);
      });
    }).catch(() => {
      return this.restorePreviousNetwork(previousNetwork).then(() => {
        const previousRegUrl = getRegisteredUrl(this.window, this.apiConfig, this.localNetworkInfo, previousNetwork.networkId);
        this.localNetworkInfo.setSuppInfo('themeId', _.get(previousRegUrl, ['id'], ''));
        return Promise.all([
          firstValueFrom(this.NetworkInfo.get()).then((data: any) => {
            this.localNetworkInfo.setInfo(data);
          }),
          firstValueFrom(this.electionApi.list({includeTests: true})).then((data: any) => {
            this.localNetworkInfo.setSuppInfo('elections', data.results);
          })
        ]);
      }).finally(() => {
        this.setNetworkSwitchInProgress(false);
      });
    });
  }

  private setNetworkSwitchInProgress(inProgress: boolean): void {
    this.authHttpBuffer.networkSwitchInProgress = inProgress;
    const body = this.document.body;
    if (!body) {
      return;
    }
    if (inProgress) {
      body.classList.add('networkSwitching');
    } else {
      body.classList.remove('networkSwitching');
    }
  }

  private reloadApp(): void {
    this.adnFormState.deregister();
    this.window.location.reload();
  }

  private restorePreviousNetwork(previousNetwork: any): any {
    this.localUserProfile.set('defaultNetworkTagId', previousNetwork.defaultNetworkTagId);
    this.LocalNetworkProfile.beginNetworkSwitch(previousNetwork.tagId, previousNetwork.networkId);
    return this.LocalNetworkProfile.refresh();
  }
}
